import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Edit, Briefcase, Printer, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatFCFA } from "../lib/fcfa";
import { useAuth } from "../contexts/AuthContext";
import { printAttestation } from "../lib/documents";

const STATUS_BADGE = {
  actif: "bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30",
  inactif: "bg-[#616161]/10 text-[#616161] border-[#616161]/30",
  conge: "bg-[#F57F17]/10 text-[#F57F17] border-[#F57F17]/30",
};
const empty = {
  first_name: "", last_name: "", position: "", department: "", salary: "",
  contract_type: "CDI", contract_start: "", contract_end: "",
  phone: "", email: "", address: "", status: "actif",
  cnss_number: "", tax_id: "", bank_account: "",
};

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/employees").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...empty, ...e, salary: String(e.salary) });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, salary: parseInt(form.salary, 10),
      contract_start: form.contract_start || null,
      contract_end: form.contract_end || null,
    };
    try {
      if (editing) {
        await api.patch(`/employees/${editing.id}`, payload);
        toast.success("Employé modifié");
      } else {
        await api.post("/employees", payload);
        toast.success("Employé créé");
      }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const onDel = async (id) => {
    if (!confirm("Supprimer cet employé ?")) return;
    await api.delete(`/employees/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        overline="Ressources humaines"
        title="Employés"
        description="Gestion du personnel administratif et technique."
        actions={
          <Button onClick={openCreate} data-testid="create-employee-btn" className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Nouvel employé
          </Button>
        }
      />

      <div className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead className="bg-[#F4F5F7] text-[#616161] tracking-overline text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3">Poste</th>
                <th className="text-left px-4 py-3">Département</th>
                <th className="text-left px-4 py-3">Contrat</th>
                <th className="text-right px-4 py-3">Salaire</th>
                <th className="text-left px-4 py-3">Statut</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E0E0E0]">
              {list.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#616161]">Aucun employé.</td></tr>
              ) : list.map((e) => (
                <tr key={e.id} className="hover:bg-[#F4F5F7]/50" data-testid={`employee-row-${e.id}`}>
                  <td className="px-4 py-3 font-medium">{e.first_name} {e.last_name}</td>
                  <td className="px-4 py-3">{e.position}</td>
                  <td className="px-4 py-3 text-[#616161]">{e.department || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{e.contract_type}</Badge></td>
                  <td className="px-4 py-3 text-right font-mono">{formatFCFA(e.salary)}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`${STATUS_BADGE[e.status]} border`}>{e.status}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => printAttestation(user.club, e)} title="Attestation">
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onDel(e.id)} data-testid={`delete-employee-${e.id}`}><Trash2 className="w-4 h-4 text-[#C62828]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier" : "Nouvel employé"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom *</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-firstname" /></div>
              <div><Label>Nom *</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-lastname" /></div>
              <div><Label>Poste *</Label><Input required value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="emp-position" /></div>
              <div><Label>Département</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
              <div><Label>Salaire (FCFA) *</Label><Input type="number" required min={0} value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} data-testid="emp-salary" /></div>
              <div>
                <Label>Type contrat</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger data-testid="emp-contract"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["CDI", "CDD", "Stage", "Benevole"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Début contrat</Label><Input type="date" value={form.contract_start} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} /></div>
              <div><Label>Fin contrat</Label><Input type="date" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>N° CNSS</Label><Input value={form.cnss_number} onChange={(e) => setForm({ ...form, cnss_number: e.target.value })} /></div>
              <div><Label>N° fiscal</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} /></div>
              <div className="col-span-2"><Label>Compte bancaire</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></div>
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="conge">En congé</SelectItem>
                    <SelectItem value="inactif">Inactif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="employee-submit-btn">
                {editing ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
